import { EntityState, PaginationResult } from 'shared/types';

import { FailureActionPayload } from 'store/errors/types';
import { MultiSelectOption } from '@hqo/multi-select';
import { StandardPaginationResult } from 'shared/types/standard-pagination-result';
import { SpecialVisitType, VisitorFrequencyEnum } from 'components/visit-form/types';
import { MonthlyMode } from 'components/create-visit-form-v2/types/form-data.types';
import { DynamicFormDto } from '@hqo/dynamic-forms-sdk/types';
import { DynamicFormDataDto } from '@hqo/dynamic-forms-sdk/types/form/form-data.types';
import { VisitorType } from 'store/api-client/api-client.types';

export interface VisitsState {
  upcomingVisitsData: VisitState[];
  upcomingVisitsTotal: number | null;

  pastVisitsData: VisitState[];
  pastVisitsTotal: number | null;

  allVisitsData: VisitState[];
  allVisitsTotal: number | null;

  mappedData: Record<string, VisitState>;
  confirmationCache: Record<string, Visit>;
  loading: boolean;
  visitorRegistrationInitialized: boolean;

  createVisitQueryState: CreateVisitQueryState;
  /** When true, next create success uses list redirect instead of V2 confirmation route (edit contact replace). */
  suppressVisitConfirmationRedirect: boolean;
  filters: VisitsFiltersState;
  filtersPinned: boolean;
  /** Bumped by `invalidateVisits` (activity SSE stream) to trigger a background list re-read. */
  invalidationToken: number;
  visitSeries: VisitSeriesState;
}

export interface CreateVisitQueryState {
  loading: boolean;
  error: Error | null;
}

export interface VisitsFiltersState {
  search: string;
  status: VisitStatus[];
  companyUuids: string[];
  companyOptions: MultiSelectOption[];
  hostUuids: string[];
  hostOptions: MultiSelectOption[];
}

export type VisitState = EntityState<Visit | null>;

export type FetchVisitsActionParams = FetchSpecificVisitsActionParams;

export interface FetchSpecificVisitsActionParams {
  buildingId: string;
  limit?: number;
  offset?: number;
  before?: string;
  after?: string;
  /**
   * A silent re-read fired by the activity SSE stream, not a user-initiated load. The shared
   * `visits.loading` flag ignores it, so an idle refresh never collapses the list to skeletons or
   * greys out an open dialog — a pure background data refresh. Stripped before the outbound request.
   */
  background?: boolean;
}

export interface VisitParams {
  buildingId: string;
  visitId: string;
  /**
   * When true, {@link cancelVisitRedirect} skips navigation (e.g. cancel + create fired together from edit flow).
   */
  skipPostCancelRedirect?: boolean;
}

export interface GroupVisitParams {
  buildingId: string;
  groupVisitUuid: string;
  /**
   * When true, {@link cancelVisitRedirect} skips navigation (e.g. cancel + create fired together from edit flow).
   */
  skipPostCancelRedirect?: boolean;
}

export interface FetchVisitResponse {
  params: VisitParams;
  result: Visit | null;
}

export interface VisitError extends FailureActionPayload {
  params: VisitParams;
}

export interface CancelVisitResult {
  canceled: boolean;
  errors?: Record<string, unknown>;
  message?: string;
}

export interface FetchVisitsParams {
  buildingId: string;
  limit: number;
  offset: number;
  before?: string;
  after?: string;
  host_uuid?: string | string[];
  host_uuids?: string[];
  host_company_uuids?: string[];
  statuses?: string[];
  q?: string;
}

export type PaginationVisits = PaginationResult<Visit> | StandardPaginationResult<Visit, 'visits'>;

export interface FetchVisitsResponse {
  params: FetchSpecificVisitsActionParams;
  result: PaginationVisits;
}

export interface FetchVisitsError extends FailureActionPayload {
  params: FetchSpecificVisitsActionParams;
}

export enum VisitStatus {
  INVITED = 'INVITED',
  CHECKED_IN = 'CHECKED_IN',
  CHECKED_OUT = 'CHECKED_OUT',
  CANCELED = 'CANCELED',
  AWAITING_APPROVAL = 'AWAITING_APPROVAL',
  VISIT_APPROVED = 'VISIT_APPROVED',
  VISIT_DENIED = 'VISIT_DENIED',
  PREREGISTERED = 'PREREGISTERED',
  NO_SHOW = 'NO_SHOW',
}

export interface Visit {
  id: string;
  alt_contact?: string;
  email: string;
  first_name: string;
  last_name: string;
  visit_status: VisitStatus;
  arrival_date: Date;
  visitors?: Visitor[];
  rrule?: string;
  user_id: number | string;
  company_name?: string;
  departure_date?: Date;
  floor?: string;
  suite?: string;
  phone?: string;
  special_visit_type?: SpecialVisitType | null;
  special_status?: string;
  escort_id?: string;
  host: Host;
  visit_type_id?: string | null;
  special_instruction_for_visitor?: string;
  special_instruction_for_receptionist?: string;
  reason_for_visit?: string;
  email_recipients?: string[];
  notify_on_check_in?: boolean;
  send_invitation_to_host?: boolean;
  frequency?: VisitorFrequencyEnum;
  end_date?: Date;
  count?: number;
  custom_fields?: DynamicFormDataDto;
  custom_form?: DynamicFormDto;
  license_plate?: string;
  pass_entry_option?: string;
  status?: VisitStatus;
  description?: string;
  email_opt_in?: boolean;
  sms_opt_in?: boolean;
  vendor_company_uuid?: string | null;
  is_group_visit?: boolean;
  group_visit_uuid?: string | null;
  recurrence_days?: {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  };
  monthly_mode?: MonthlyMode | null;
  monthly_on_last_day?: boolean | null;
  monthly_by_day?: { position: number; weekday: string } | null;
  series_uuid?: string | null;
  visit_series_uuid?: string | null;
  series_type?: string | null;
  series_dates?: string[];
}

export interface Host {
  first_name: string;
  last_name: string;
  email: string;
  company_name?: string;
}

export interface Visitor {
  first_name: string;
  last_name: string;
  visitor_phone?: string;
  id?: string | null;
  email?: string;
  phone?: string;
  type: VisitorType;
  image_url?: string;
  company?: string;
  is_premier?: boolean;
  email_recipients?: string[];
  photo_uuid?: string | null;
  email_opt_in?: boolean;
  sms_opt_in?: boolean;
}

export type CreateVisitor = Omit<Visitor, 'type'>;

export enum VisitUiType {
  PAST = 'past',
  UPCOMING = 'upcoming',
  ALL = 'all',
}

export interface CreateVisitDto {
  id?: string;
  user_image_url?: string;
  user_id?: number | string;
  first_name?: string;
  last_name?: string;
  email?: string;
  company_name?: string;
  arrival_date?: Date;
  departure_date?: Date | null;
  purpose_id?: number | string;
  start_date?: Date;
  end_date?: Date;
  floor?: string;
  suite?: string;
  visit_type_id?: string;
  special_visit_type?: SpecialVisitType;
  special_instruction_for_escort?: string;
  special_instruction_for_receptionist?: string;
  special_instruction_for_visitor?: string;
  reason_for_visit?: string;
  notify_on_check_in?: boolean;
  check_in_notification_list?: string[];
  rrule?: string;
  visitors: CreateVisitor[];
  checked_in?: Date;
  checked_out?: Date;
  alt_contact?: string;
  status?: number;
  host_email?: string;
  phone?: string;
  escort_id?: string;
  email_recipients?: string[];
  frequency: VisitorFrequencyEnum;
  count: number;
  custom_fields?: DynamicFormDataDto;
  license_plate?: string;
  pass_entry_option?: string;
  send_invitation_to_host?: boolean;
  description?: string;
  duration?: number;
  email_opt_in?: boolean;
  sms_opt_in?: boolean;
  recurrence_days?: {
    monday?: boolean;
    tuesday?: boolean;
    wednesday?: boolean;
    thursday?: boolean;
    friday?: boolean;
    saturday?: boolean;
    sunday?: boolean;
  };
  monthly_mode?: MonthlyMode;
  visit_dates?: string[];
}

export interface CreateVisitParams {
  buildingId: string;
  visitDto: CreateVisitDto;
  /**
   * When true (e.g. edit flow after primary visitor email/phone change), skip the V2 "Visit confirmed" route
   * and navigate like a normal save. Not sent to the API.
   */
  suppressVisitConfirmationRedirect?: boolean;
}

export interface AdditionalOccurrence {
  arrival_time: string | Date;
  departure_time: string | Date;
}

export interface UpdateVisitDto {
  id: string;
  alt_contact?: string | null;
  email: string;
  first_name: string;
  last_name: string;
  visit_status: string;
  arrival_date: Date;
  visitors?: CreateVisitor[] | null;
  rrule?: string | null;
  user_id: number | string;
  company_name?: string | null;
  departure_date?: Date | null;
  floor?: string | null;
  suite?: string | null;
  phone?: string | null;
  special_visit_type?: SpecialVisitType | null;
  special_status?: string | null;
  escort_id?: string | null;
  host: Host;
  visit_type_id?: string | null;
  special_instruction_for_visitor?: string | null;
  special_instruction_for_receptionist?: string | null;
  reason_for_visit?: string | null;
  email_opt_in?: boolean;
  sms_opt_in?: boolean;
  additional_occurrences?: AdditionalOccurrence[];
}

export interface VisitSeriesDate {
  arrivalTime: Date | string;
  departureTime: Date | string;
}

export interface VisitSeries {
  seriesUuid: string;
  seriesType?: string;
  dates: VisitSeriesDate[];
}

export interface VisitSeriesState {
  loading: boolean;
  error: Error | null;
  data: VisitSeries | null;
}

export interface FetchVisitSeriesParams {
  buildingId: string;
  seriesId: string;
}

export interface FetchVisitSeriesResponse {
  params: FetchVisitSeriesParams;
  result: VisitSeries;
}

export interface VisitSeriesError extends FailureActionPayload {
  params: FetchVisitSeriesParams;
}

export interface UpdateVisitSeriesDatePayload {
  arrivalTime: string;
  departureTime: string;
}

export interface UpdateVisitSeriesVisitorPayload {
  firstName: string;
  lastName: string;
  email?: string | null;
  phone?: string | null;
}

export interface UpdateVisitSeriesPayload {
  dates?: UpdateVisitSeriesDatePayload[];
  visitors?: UpdateVisitSeriesVisitorPayload[];
  visitorNote?: string | null;
  guardNote?: string | null;
  visitPurpose?: string | null;
  emailOptIn?: boolean;
  smsOptIn?: boolean;
  floors?: Array<{ floorNumber: number; suiteLabel?: string }>;
  additionalRecipients?: {
    internal?: string[];
    external?: string[];
  };
}

export interface UpdateVisitSeriesActionParams {
  params: FetchVisitSeriesParams;
  payload: UpdateVisitSeriesPayload;
  originalDates?: string[];
}

export interface UpdateVisitSeriesResponse {
  params: FetchVisitSeriesParams;
  result: VisitSeries;
}

export interface MultiDayCreateVisitItem {
  id?: string;
  uuid?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  visitor_name?: string;
  visit_status?: VisitStatus;
  status?: VisitStatus;
  arrival_date?: Date | string;
  arrival_time?: Date | string;
  departure_date?: Date | string;
  departure_time?: Date | string;
  visitors?: Visitor[];
  series_uuid?: string;
  visit_series_uuid?: string;
  series_type?: string;
}

export interface MultiDayCreateVisitsResponse {
  visits: MultiDayCreateVisitItem[];
  series_uuid?: string;
  series_type?: string;
  series_status?: string;
  series_name?: string;
}

export interface UpdateVisitActionParams {
  params: VisitParams;
  payload: Partial<UpdateVisitDto>;
  /**
   * When true, {@link updateVisitRedirect} returns to the visit's own pane view instead of the
   * visits list — in the activity-center pane the list is redundant (the pane already shows it).
   */
  isActivityPane?: boolean;
}

export interface UpdateVisitResponse {
  params: VisitParams;
  result: Visit;
  isActivityPane?: boolean;
}
